<template>
    <VJoyFormGroup :data-testid="`${dataTestid}-container`">
        <VJoyInputPassword
            v-model="modelValue"
            :invalid="isFieldInvalidState"
            v-bind="reactive({...props, ...$attrs})"
            :data-testid="dataTestid"
            @focus="isFocused = true"
            @blur="isFocused = false"
        >
            <template #password-requirements>
                <VJoyPasswordRequirement data-testid="password-requirement-min-length" :status="passwordLengthStatus" :label="minLengthLabel" />
                <VJoyPasswordRequirement
                    data-testid="password-requirement-uppercase"
                    :status="passwordUpperCaseStatus"
                    :label="containsUppercaseLabel"
                />
                <VJoyPasswordRequirement
                    data-testid="password-requirement-special"
                    :status="passwordSpecialStatus"
                    :label="containsSpecialCharacterLabel"
                />
            </template>
        </VJoyInputPassword>
    </VJoyFormGroup>
</template>

<script setup lang="ts">
    import {computed, reactive, ref} from 'vue';
    import type {RegleFieldStatus, RegleRuleDecl, RegleEnforceRequiredRules} from '@regle/core';
    import {isFilled, minLength, requiredIf, withMessage, containsUppercase, containsSpecialCharacter} from '@regle/rules';
    import {useTranslation} from '#imports';
    import {useRegle} from '@malt/forms';
    import {
        VJoyInputPassword,
        VJoyPasswordRequirement,
        VJoyFormGroup,
        type VJoyInputProps,
        type TVJoyPasswordRequirementStatus,
    } from '@maltjoy/core-vue';

    defineOptions({inheritAttrs: false});

    const {t} = useTranslation();

    const PASSWORD_RULES_KEYS = ['minLength', 'containsUppercase', 'containsSpecialCharacter'] as const;
    type PasswordRulesKey = (typeof PASSWORD_RULES_KEYS)[number];

    interface MaltPasswordInputProps extends VJoyInputProps {
        /**
         * Provide parent field status from the useRegle composable so component can add password related rules
         *
         * @example
         * ```ts
         * const {r$} = useRegle({password: ''}, {
         *     password: {
         *         required,
         *     },
         * });
         * ```
         *
         * ```vue
         * <template>
         *     <MaltPasswordInput
         *         :field="r$.password"
         *         ...
         *     />
         * </template>
         * ```
         */
        field?: RegleFieldStatus<string | undefined>;
        /**
         * Whether the password should be required (by Regle)
         */
        required?: boolean;
        /**
         * Provide a data-testid that will be applied to the root element
         */
        dataTestid?: string;
    }

    const {type, field, invalid, dataTestid = 'password-input', required, modelValue: _, ...props} = defineProps<MaltPasswordInputProps>();

    const modelValue = defineModel<string | undefined>();

    // --- Focus state ---

    const isFocused = ref(false);

    // --- Validation ---

    /**
     * Represent the rules needed to validate a password fields.
     * It will either be injected into the parent instance, or defined locally.
     */
    const rules = {
        requiredIf: withMessage(
            requiredIf(() => required),
            t('generic.forms.validation.errors.required'),
        ),
        minLength: minLength(8),
        containsUppercase,
        containsSpecialCharacter,
    } satisfies RegleRuleDecl<string>;

    if (field) {
        field.$addRules(rules);
    }

    /** Fallback Regle validation if no field prop is provided */
    const {r$: fallbackR$} = useRegle(modelValue, rules, {id: 'malt-password-input-default', disabled: () => !!field});

    /** Resolve the field to use for the password input. If a field prop is provided, use it, otherwise use the fallback field. */
    const resolvedField = computed(() => {
        if (field != null) {
            return field as RegleFieldStatus<
                string | undefined,
                RegleEnforceRequiredRules<'minLength' | 'containsUppercase' | 'containsSpecialCharacter'>
            >;
        }
        return fallbackR$;
    });

    /**
     * Whether the field should be displayed in a invalid state
     */
    const isFieldInvalidState = computed<boolean>(() => {
        if (isFocused.value) {
            return false;
        }
        return invalid || resolvedField.value.$error;
    });

    /**
     * Compute which TJoyPasswordRequirementStatus to display for a given password rule
     */
    function computePasswordRuleStatus(rule: PasswordRulesKey): TVJoyPasswordRequirementStatus {
        if (fieldErrors.value.length > 0 && !isFocused.value) {
            return 'error';
        } else if (isFilled(resolvedField.value.$value) && resolvedField.value.$rules[rule].$valid) {
            return 'success';
        } else if (!isFocused.value && isFilled(resolvedField.value.$value) && resolvedField.value.$dirty) {
            return 'error';
        }
        return 'neutral';
    }

    /** Return the TJoyPasswordRequirementStatus for the minLength rule */
    const passwordLengthStatus = computed(() => computePasswordRuleStatus('minLength'));
    /** Return the TJoyPasswordRequirementStatus for the containsUppercase rule */
    const passwordUpperCaseStatus = computed(() => computePasswordRuleStatus('containsUppercase'));
    /** Return the TJoyPasswordRequirementStatus for the containsSpecialCharacter rule */
    const passwordSpecialStatus = computed(() => computePasswordRuleStatus('containsSpecialCharacter'));

    // --- Requirements Status labels

    const minLengthLabel = computed(() => t('generic.forms.tooltips.password.minLength', {count: resolvedField.value.$rules.minLength.$params?.[0]}));
    const containsUppercaseLabel = computed(() =>
        t('generic.forms.tooltips.password.containsUppercase', {count: resolvedField.value.$rules.containsUppercase.$metadata.minUppercaseCount}),
    );
    const containsSpecialCharacterLabel = computed(() =>
        t('generic.forms.tooltips.password.containsSpecialCharacter', {
            count: resolvedField.value.$rules.containsSpecialCharacter.$metadata.minCharactersCount,
        }),
    );

    // --- Other validation errors

    /**
     * Display only errors not related to password rules (like required)
     */
    const fieldErrors = computed<string[]>(() => {
        if (isFocused.value) {
            return [];
        }
        return resolvedField.value.$issues
            .filter((issue) => !PASSWORD_RULES_KEYS.includes(issue.$rule as PasswordRulesKey))
            .map((issue) => issue.$message);
    });

    defineExpose({r$: fallbackR$});
</script>
